import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class ChildCategories extends Component {
  state = {
    child_categories: [],
    categories: [],
    filtered_sub_categories: [],
    formData: {
      id: null,
      category_id: "",
      sub_category_id: "",
      child_category_name: "",
      child_category_status: "",
      child_category_img: null,
    },
    imagePreview: "",
    error: "",
    loading: false,
    submitting: false,
    modalMode: "add",
    deleteId: null,
  };

  componentDidMount() {
    this.fetchCategories();
    this.fetchChildCategories();

    const modalEl = document.getElementById("child_categoryModal");
    if (modalEl) modalEl.addEventListener("hidden.bs.modal", this.resetForm);
  }

  /** ✅ Fetch all Categories */
  fetchCategories = async () => {
    try {
      const res = await axios.get("/products/categories");
      this.setState({ categories: res.data?.data || [] });
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  /** ✅ Fetch all Child Categories */
  fetchChildCategories = async () => {
    this.setState({ loading: true });
    try {
      const res = await axios.get("/products/child_categories");
      this.setState({ child_categories: res.data?.data || [] });
    } catch (err) {
      console.error("Failed to fetch child categories:", err);
    } finally {
      this.setState({ loading: false });
    }
  };

  /** ✅ Fetch Sub Categories by Category ID */
  fetchSubCategories = async (category_id) => {
    if (!category_id) {
      this.setState({ filtered_sub_categories: [] });
      return;
    }
    try {
      const res = await axios.get(`/products/sub_categories/byCategory/${category_id}`);
      this.setState({ filtered_sub_categories: res.data?.data || [] });
    } catch (err) {
      console.error("Failed to fetch sub categories:", err);
      this.setState({ filtered_sub_categories: [] });
    }
  };

  /** ✅ Handle input changes */
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => {
      const updatedForm = { ...prev.formData, [name]: value };

      if (name === "category_id") {
        updatedForm.sub_category_id = "";
        this.fetchSubCategories(value);
      }

      return { formData: updatedForm };
    });
  };

  /** ✅ Handle file input */
  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.setState((prev) => ({
        formData: { ...prev.formData, child_category_img: file },
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  /** ✅ Reset form state */
  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        category_id: "",
        sub_category_id: "",
        child_category_name: "",
        child_category_status: "",
        child_category_img: null,
      },
      imagePreview: "",
      filtered_sub_categories: [],
      modalMode: "add",
      error: "",
      submitting: false,
    });
  };

  /** ✅ Open modal (Add/Edit) */
  openModal = (mode = "add", data = null) => {
    const modal = new window.bootstrap.Modal(document.getElementById("child_categoryModal"));
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    if (mode === "edit" && data) {
      this.fetchSubCategories(data.category_id);
      this.setState({
        modalMode: "edit",
        formData: {
          id: data.id,
          category_id: data.category_id || "",
          sub_category_id: data.sub_category_id || "",
          child_category_name: data.child_category_name,
          child_category_status: data.child_category_status?.toString() ?? "1",
          child_category_img: null,
        },
        imagePreview: data.child_category_img
          ? `${baseUrl}/uploads/child_categories/${data.child_category_img}`
          : "",
      });
    } else {
      this.resetForm();
    }

    modal.show();
  };

  /** ✅ Submit form (Add / Update) */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;
    const token = localStorage.getItem("token");

    if (!formData.category_id)
      return this.setState({ error: "Category is required" });
    if (!formData.sub_category_id)
      return this.setState({ error: "Sub Category is required" });
    if (!formData.child_category_name)
      return this.setState({ error: "Child Category name is required" });
    if (formData.child_category_status === "")
      return this.setState({ error: "Status is required" });

    const data = new FormData();
    data.append("category_id", formData.category_id);
    data.append("sub_category_id", formData.sub_category_id);
    data.append("child_category_name", formData.child_category_name);
    data.append("child_category_status", formData.child_category_status);
    if (formData.child_category_img)
      data.append("child_category_img", formData.child_category_img);

    this.setState({ submitting: true, error: "" });

    try {
      if (modalMode === "add") {
        await axios.post("/products/child_categories", data, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        await axios.post(
          `/products/child_categories/${formData.id}?_method=PUT`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      await this.fetchChildCategories();
      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("child_categoryModal")
      );
      modal.hide();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "Child Category Added!"
            : "Child Category Updated!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving child category.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** ✅ Delete Confirmation */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modal = new window.bootstrap.Modal(document.getElementById("deleteModal"));
    modal.show();
  };

  /** ✅ Delete Item */
  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      await axios.delete(`/products/child_categories/${deleteId}`);
      await this.fetchChildCategories();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("deleteModal")
      );
      modal.hide();

      Swal.fire({
        icon: "success",
        title: "Child Category deleted!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Failed to delete.",
      });
    }
  };

  render() {
    const {
      child_categories,
      categories,
      filtered_sub_categories,
      formData,
      imagePreview,
      modalMode,
      loading,
      error,
      submitting,
    } = this.state;

    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="card shadow-none">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5>Child Categories</h5>
              <button
                className="btn btn-light-primary"
                onClick={() => this.openModal("add")}
              >
                + New Child Category
              </button>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">Loading...</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Category</th>
                        <th>Sub Category</th>
                        <th>Child Category</th>
                        <th>Image</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {child_categories.length ? (
                        child_categories.map((item, i) => (
                          <tr key={item.id}>
                            <td>{i + 1}</td>
                            <td>{item.category_name || "N/A"}</td>
                            <td>{item.sub_category_name || "N/A"}</td>
                            <td>{item.child_category_name}</td>
                            <td>
                              {item.child_category_img ? (
                                <img
                                  src={`${baseUrl}/uploads/child_categories/${item.child_category_img}`}
                                  alt="img"
                                  style={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                "No Image"
                              )}
                            </td>
                            <td>
                              {item.child_category_status == 1 ? (
                                <span className="badge bg-success">Active</span>
                              ) : (
                                <span className="badge bg-danger">
                                  Inactive
                                </span>
                              )}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-light-success me-2"
                                onClick={() => this.openModal("edit", item)}
                              >
                                <i className="ph ph-note-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-light-danger"
                                onClick={() => this.confirmDelete(item.id)}
                              >
                                <i className="ph ph-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No data found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <div className="modal fade" id="child_categoryModal" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === "add"
                      ? "Add Child Category"
                      : "Edit Child Category"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>

                <div className="modal-body">
                  {/* Category */}
                  <div className="mb-3">
                    <label className="form-label">Category</label>
                    <select
                      name="category_id"
                      className="form-select"
                      value={formData.category_id}
                      onChange={this.handleChange}
                    >
                      <option value="">-- Select Category --</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sub Category */}
                  <div className="mb-3">
                    <label className="form-label">Sub Category</label>
                    <select
                      name="sub_category_id"
                      className="form-select"
                      value={formData.sub_category_id}
                      onChange={this.handleChange}
                      disabled={!formData.category_id}
                    >
                      <option value="">-- Select Sub Category --</option>
                      {filtered_sub_categories.map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.sub_category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Name */}
                  <div className="mb-3">
                    <label className="form-label">Child Category Name</label>
                    <input
                      type="text"
                      name="child_category_name"
                      className="form-control"
                      value={formData.child_category_name}
                      onChange={this.handleChange}
                    />
                  </div>

                  {/* Status */}
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="child_category_status"
                      className="form-select"
                      value={formData.child_category_status}
                      onChange={this.handleChange}
                    >
                      <option value="">Select Status</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>

                  {/* Image */}
                  <div className="mb-3">
                    <label className="form-label">Image</label>
                    <input
                      type="file"
                      className="form-control"
                      onChange={this.handleFileChange}
                    />
                    {imagePreview && (
                      <div className="mt-2 text-center">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ width: "100px", borderRadius: "8px" }}
                        />
                      </div>
                    )}
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-light-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving..."
                      : modalMode === "add"
                      ? "Add"
                      : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-body text-center p-4">
                <i className="ph ph-warning text-danger f-40 mb-3"></i>
                <h5>Are you sure?</h5>
                <p className="text-muted mb-4">This action cannot be undone.</p>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    onClick={this.handleDelete}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ChildCategories;
